import PropTypes from "prop-types";
import { Fragment, useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@Common/helpers/Modifiers/formatters";

// Defaults builders
import { CommissionTier, DurationTier } from "@PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@Common/hooks/CallbackState";
import { useValidations } from "@Common/hooks/Validations";

// Layouts
import DefaultTiersLoader from "@Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import CustomizedSwitches from "@Layouts/components/InputComponents/CustomizedSwitches";

// Components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";
import Queues from "@PayPlans/components/Queues";

// Tiers Layouts
import TierAlertError from "@Layouts/components/Tiers/TierAlertError";
import TierRows from "@Layouts/components/Tiers/TierRows";
import TierColumns from "@Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@Layouts/components/Tiers/TierColumnsItem";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
// MUI
import { FormControl, FormControlLabel, Paper, Radio, RadioGroup, Typography } from "@mui/material";

/*
	average turnaround time max value = 744
	average turnaround time min value = 0

	unattended calls max value = 100
	unattended calls time min value = 0

	commission percentage max value = 100
	commission percentage min value = 0

	commission dollars max value = - infinity
	commission dollars min value = + infinity
*/
const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const FONT_FAMILY = "var(--font-family-v1)";

const getErrorsDefinition = (errors = new Map(), identifier = "") => {
	if (!identifier) {
		return undefined;
	}
	return errors?.get(identifier);
};

const isSomeQueueDefined = (queues) => {
	const { hasQueues = false, individual = false } = queues;
	return hasQueues || individual;
};

export default function CallActivityPerformance(props) {
	const {
		sectionId = "callActivityPerformance",
		performance = null,
		paySwitchIsEditable = false,
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		useSectionsErrors = () => {},
	} = props;

	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const headerErrors = getErrorsDefinition(errors, "time");
	const rowsErrors = getErrorsDefinition(errors, "calls");
	const columnsErrors = getErrorsDefinition(errors, "commissions");
	const queueDefinitionError = Boolean(getErrorsDefinition(errors, "queue")?.length);
	const minimumError = Boolean(getErrorsDefinition(errors, "minTiersRequired")?.length);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onCallActivityPerformanceUpdate = (updatedCallActivity) => {
		const isSectionValid = isValid && isValidChecks;
		isSectionValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isSectionValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedCallActivity);
		}
	};

	const [callActivity, setCallActivity] = useCallbackState(null, onCallActivityPerformanceUpdate);

	// Methods
	const handleAddColumn = () => {
		const tiers = [...callActivity.tiers];
		const commissionInput = tiers?.[0]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";

		tiers.forEach((tier) => {
			tier.commissions.push(DurationTier({ from: 0, to: 0, commission: 0, commissionInput }));
		});

		wasUpdated.current = false;
		setCallActivity({ ...callActivity, tiers });
	};

	const handleDeleteColumn = () => {
		const tiers = [...callActivity.tiers];
		if (tiers?.[0]?.["commissions"]?.length > minimumColumns) {
			tiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = tier?.commissions?.length ? tier.commissions.length - 1 : 0;
				tier.commissions[lastIndex]["to"]["value"] = 0;
			});
		}
		update({ ...callActivity, tiers });
	};

	const handleAddTier = () => {
		const tiers = [...callActivity.tiers];
		const lastIndex = tiers?.length ? tiers.length - 1 : 1;
		const lastInput = tiers?.[lastIndex]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";

		const previousCommissionsLength = tiers?.[lastIndex]?.["commissions"]?.length;

		const commissions = [];
		for (let i = 0; i < previousCommissionsLength; i++) {
			const previousFromDuration = tiers?.[0]?.["commissions"]?.[i]?.["from"]?.["value"] || 0;
			const previousToDuration = tiers?.[0]?.["commissions"]?.[i]?.["to"]?.["value"] || 0;

			commissions[i] = DurationTier({
				from: previousFromDuration,
				to: previousToDuration,
				commission: 0,
				commissionInput: lastInput,
			});
		}

		tiers.push(CommissionTier(0, 0, commissions));

		if (tiers?.length) {
			tiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = false;
		setCallActivity({ ...callActivity, tiers });
	};

	const handleDeleteTier = () => {
		const tiers = [...callActivity.tiers];
		if (tiers.length >= 1) {
			tiers.splice(-1);
		}
		const lastIndex = tiers?.length ? tiers.length - 1 : 1;
		tiers[lastIndex].to.value = 0;
		update({ ...callActivity, tiers });
	};

	const handleUpdateInput = (_row, _column, location, area, input) => {
		const tiers = [...callActivity.tiers];

		if (paySwitchIsEditable && input === "Dollar") {
			callActivity["grossProfitSource"] = "SUBTOTAL";
		}

		switch (location) {
			case "column":
				break;
			case "row":
				tiers.forEach((tier) => {
					tier.commissions?.forEach((commission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}

		update({ ...callActivity, tiers });
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const tiers = [...callActivity.tiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				tiers.forEach((tier) => {
					tier.commissions?.forEach((commission, key) => {
						if (area in commission && key === column) {
							commission[area]["value"] = updatedValue;
							if (tier.commissions?.[key + 1]) {
								tier.commissions[key + 1]["from"]["value"] = updatedValue;
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "commission") {
					tiers[row][area]["value"] = updatedValue;
					if (tiers?.[row + 1] && area === "to") {
						tiers[row + 1]["from"]["value"] = (updatedValue - 0.01).toFixed(2);
					}
				} else {
					tiers[row]["commissions"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update({ ...callActivity, tiers });
	};

	const handleChangeCheckbox = (area, value) => {
		const updated = { ...callActivity };
		updated[area] = value;
		update(updated);
	};

	const handleUpdateGrossProfitSourceSwitch = (value) => {
		const updated = { ...callActivity };
		let valueToUse = null;
		if (value) {
			valueToUse = "GROSSES";
		} else {
			valueToUse = "SUBTOTAL";
		}
		updated["grossProfitSource"] = valueToUse;
		update(updated);
	};

	const update = (callActivityPerformance) => {
		setCallActivity(callActivityPerformance);
		wasUpdated.current = true;
	};

	const isValidChecks = useMemo(() => callActivity?.hasQueues || callActivity?.individual, [callActivity]);

	useMemo(() => {
		if (callActivity?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "calls",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">", // If from > to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: callActivity?.tiers || [],
				},
				{
					definition: {
						useKey: "time",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">", // If from > to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Duration: 0 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Turnaround time invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: callActivity?.tiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper <= lower is good,
									deltas: { Dollar: 0, Percent: 0 },
								},
								{
									order: "DIAGONAL",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: callActivity?.tiers,
				},
				{
					definition: {
						useKey: "minTiersRequired",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Please add at least a two tiers configuration",
						operators: {
							customFn: (params = null) => {
								const { tiers } = params || {};
								const isValidRows = tiers?.length >= 2;
								const isValidColumns = tiers?.[0]?.commissions?.length >= 2;
								return [{ tiers: !(isValidRows || isValidColumns) }];
							},
							params: {
								tiers: callActivity?.tiers,
							},
						},
					},
					section: [{ tiers: false }],
				},
			]);
		}
	}, [callActivity, validate]);

	useEffect(() => {
		const base = clone(performance);
		setCallActivity({ ...base, evaluateBy: base?.evaluateBy ?? "COMMISSION_CYCLE" });
		return () => {
			wasUpdated.current = false;
			setCallActivity(null);
			clearSectionErrors(sectionId);
		};
	}, [sectionId, performance, setCallActivity, clearSectionErrors]);

	const handleEvaluateByChange = (e) => {
		update({ ...callActivity, evaluateBy: e.target.value });
	};

	return (
		<Grid container mt={2} px={"32px"}>
			{loading ? (
				<DefaultTiersLoader buttons sx={{ padding: 0 }} />
			) : (
				<Fragment>
					<Grid container item xs={12}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="flex-start"
							gap={2}
							width={1}
							sx={{ minHeight: 40, mb: 2 }}
						>
							<Typography className="!font-sans !text-[14.5px] text-default">Evaluate by:</Typography>
							<Stack>
								<FormControl>
									<RadioGroup
										row
										aria-labelledby="range-buttons-group"
										value={callActivity?.evaluateBy ?? "COMMISSION_CYCLE"}
										name="range-buttons-group"
										className={"radio-buttons-small regular-blue-color"}
										onChange={handleEvaluateByChange}
										disabled={readOnly}
										sx={{
											"& .MuiFormControlLabel-root": {
												marginRight: "24px",
											},
										}}
									>
										<FormControlLabel
											disabled={readOnly}
											value="COMMISSION_CYCLE"
											control={<Radio />}
											label="Commission Cycle"
											sx={{
												".MuiFormControlLabel-label": {
													fontFamily: FONT_FAMILY,
													color: "var(--text-color)",
													fontSize: "14.5px",
													whiteSpace: "nowrap",
												},
											}}
										/>
										<FormControlLabel
											disabled={readOnly}
											value="PAY_PERIOD"
											control={<Radio />}
											label="Pay Period"
											sx={{
												"& .MuiTypography-root": {
													fontFamily: FONT_FAMILY,
													color: "var(--text-color)",
													fontSize: "14.5px",
													whiteSpace: "nowrap",
												},
											}}
										/>
									</RadioGroup>
								</FormControl>
							</Stack>
							<Stack my={1.5} sx={{ ml: "600px", width: "150px" }}>
								<CustomizedSwitches
									disabled={
										readOnly ||
										!paySwitchIsEditable ||
										callActivity?.tiers?.[0]?.commissions?.[0]?.commission?.input === "Dollar"
									}
									textLeft
									textChecked="% of Total Gross"
									textUnChecked="% of Total Pay"
									checked={Boolean(callActivity?.grossProfitSource === "GROSSES")}
									onChange={(e) => handleUpdateGrossProfitSourceSwitch(e)}
									tooltip={{
										title: "Apply over total pay or apply over total gross.",
										placement: "top",
										key: "max-payout-tooltip",
									}}
								/>
							</Stack>
						</Stack>
						<Grid item xs={12}>
							<TierColumns
								titleLabel={"Avg. Turnaround Time"}
								titleContainerProps={{
									justifyContent: "flex-start",
									alignItems: "end",
									sx: { maxWidth: "203px", width: "203px" },
								}}
								titleProps={{
									className: "tiered-title tiered-title-adjustment",
									sx: null,
								}}
								disabledDelete={callActivity?.tiers?.[0]?.commissions?.length <= minimumColumns || readOnly}
								disabledAdd={callActivity?.tiers?.[0]?.commissions?.length >= maximumColumns || readOnly}
								onClickDelete={() => handleDeleteColumn()}
								onClickAdd={() => handleAddColumn()}
							>
								<Stack direction={"row"} position={"relative"}>
									{callActivity?.tiers?.[0]?.commissions?.map((tier, key) => {
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(callActivity?.tiers?.[0]?.commissions.length === key + 1);

										return (
											<TierColumnsItem key={key} index={key} title={"Comm."}>
												<MatrixColumn
													index={key}
													item={tier}
													errors={headerErrors}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													readOnly={readOnly}
													onActionUpdate={handleUpdateValue}
												/>
											</TierColumnsItem>
										);
									})}
								</Stack>
							</TierColumns>
						</Grid>

						<Grid item xs={12} mt={1} minHeight={"200px"}>
							<TierRows
								showTitle
								title={"Unattended Calls %"}
								sxTitlePropsContainer={{
									top: 145,
									left: "-30px",
								}}
								centerTitleAfterRow={4}
								deleteButtonText={"Remove Tier"}
								addButtonText={"Add Tier"}
								deleteButtonDisabled={callActivity?.tiers?.length <= 1 || readOnly}
								addButtonDisabled={callActivity?.tiers?.length >= maximumTiers || readOnly}
								spaces={{
									mainSection: 190,
									columns: 175,
									totalColumns: callActivity?.tiers?.[0]?.commissions?.length || 1,
									totalRows: callActivity?.tiers?.length || 1,
									separation: 5.5,
								}}
								rowsAreaProps={{
									ml: "35px",
								}}
								onClickDelete={() => handleDeleteTier()}
								onClickAdd={() => handleAddTier()}
							>
								<TransitionGroup>
									<Collapse>
										<Stack direction="column" rowGap={2}>
											{callActivity?.tiers?.map((tier, key) => {
												const rowKey = `call-activity-tier-${key}`;
												const fromDisabled = Boolean(!key);
												const toDisabled = Boolean(callActivity?.tiers?.length === key + 1);
												const commissionsLength = callActivity?.tiers?.[0]?.commissions?.length || 0;
												const reduction = commissionsLength * 3.4 + 40 + commissionsLength;

												const getColumnsErrors = (nativeErrors = [], complainErrors = false) => {
													if (nativeErrors?.length) {
														return nativeErrors;
													}
													return [[{ commission: complainErrors }]];
												};

												return (
													<Grid key={rowKey} container item>
														<MatrixRow
															index={key}
															item={tier}
															fromDisabled={fromDisabled}
															toDisabled={toDisabled}
															spaces={{
																reduction,
																mainSection: 190,
																columns: 175,
																totalColumns: callActivity?.tiers?.[0]?.commissions?.length || 1,
																totalRows: callActivity?.tiers?.length || 1,
																separation: 5.5,
															}}
															rowsErrors={rowsErrors || []}
															columnsErrors={getColumnsErrors(
																columnsErrors,
																queueDefinitionError || minimumError
															)}
															readOnly={readOnly}
															sxCells={{ minWidth: "175px" }}
															onActionUpdate={handleUpdateValue}
															onChangeType={handleUpdateInput}
														/>
													</Grid>
												);
											})}
										</Stack>
									</Collapse>
								</TransitionGroup>

								{Boolean(localErrorsMessages?.length) && (
									<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
										{localErrorsMessages.map((message, key) => (
											<TierAlertError key={`error_call_activity_${message}_${key}`} text={message} />
										))}
									</Grid>
								)}
							</TierRows>
						</Grid>
					</Grid>
					<Grid container item xs={12} mt={2}>
						<Queues
							data={{ hasQueues: callActivity?.hasQueues, individual: callActivity?.individual }}
							readOnly={readOnly}
							onActionChange={handleChangeCheckbox}
						/>
					</Grid>
				</Fragment>
			)}
		</Grid>
	);
}
CallActivityPerformance.propTypes = {
	sectionId: PropTypes.string,
	performance: PropTypes.object,
	paySwitchIsEditable: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};
